import { type CancelToken } from 'axios';
import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { createCancelToken } from '~/_shared/utils/api/api.helpers';
import {
  isApiError, isCancelError,
} from '~/_shared/utils/api/apiError.helpers';
import { debounce } from '~/_shared/utils/debounce';
import { useTranslation } from '~/_shared/utils/hooks';
import type { ConstraintsValidationDataMap } from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/createBoundaryGroupFromAIModal/modalSteps/constraintsValidation/constraints.types';
import { useClientIdSelector } from '~/store/selectors/useClientIdSelector';
import { useMapIdSelector } from '~/store/selectors/useMapIdSelector';
import { usePrimarySpreadsheetId } from '~/store/selectors/usePrimarySpreadsheetId';
import { type MetricValue } from '../../createBoundaryGroupFromAIModal.types';
import { EMPTY_GROUP_ID } from '../../useGetColumnATOMetrics';
import {
  getConstraintValidationErrorMessages, getConstraintValidationKey, isLocationCount,
  isLocationCount as isTotalLocationCountMetric, normalizeEmptyGroupIdForBackend,
} from './constraints.helpers';
import {
  LOCATION_COUNT_ID, validateWMSTerritoryConstraints, type ValidateWMSTerritoryConstraintsItem,
} from './constraints.repository';

type WMSConstraintsValidationParams = {
  constraintsEnabled: boolean;
  boundaryGroupId: number | null;
  dataMetricsValues: readonly MetricValue[];
  demographicsValues: readonly MetricValue[];
  isStepActive: boolean;
  zipCodesSubset?: readonly string[];
  statesSubset?: readonly string[];
};

export type WMSConstraintValidationState = {
  areConstraintsValid: boolean;
  generalError: string;
  isLoading: boolean;
  validationData: ConstraintsValidationDataMap;
};

const executeDebounced = debounce((fn: () => void) => fn(), 750);

export const useWMSConstraintsValidation = ({
  constraintsEnabled,
  boundaryGroupId,
  demographicsValues,
  dataMetricsValues,
  isStepActive,
  zipCodesSubset,
  statesSubset,
}: WMSConstraintsValidationParams): WMSConstraintValidationState => {
  const [validationData, setValidationData] = useState<ConstraintsValidationDataMap>(new Map());
  const [generalError, setGeneralError] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [t] = useTranslation();
  const clientId = useClientIdSelector();
  const spreadsheetId = usePrimarySpreadsheetId();
  const mapId = useMapIdSelector();

  const constraints = useMemo(() => [
    ...dataMetricsValues.filter(v => v.checked),
    ...demographicsValues.filter(v => v.checked),
  ], [dataMetricsValues, demographicsValues]);

  const validateConstraints = useCallback(async (cancelToken: CancelToken) => {
    if (!clientId || mapId === null || spreadsheetId === null || boundaryGroupId === null) {
      return;
    }

    const validationItems: ValidateWMSTerritoryConstraintsItem[] = constraints.map(metricForRequest);

    if (validationItems.length === 0) {
      return;
    }

    setIsLoading(true);

    try {
      const validationResult = await validateWMSTerritoryConstraints(clientId, {
        boundary_group_id: boundaryGroupId,
        map_id: mapId,
        constraints: validationItems,
        spreadsheet_id: spreadsheetId,
        dataset_states: statesSubset,
        dataset_zip_codes: zipCodesSubset,
      }, cancelToken);

      setGeneralError('');

      if (validationResult && validationResult.results.length) {
        const newValidationData: ConstraintsValidationDataMap = new Map();

        for (const validationItem of validationResult.results) {
          const errorMessages = getConstraintValidationErrorMessages(validationItem, t);

          const validationEntry = {
            errorMessages,
            highestMin: validationItem.limits.highestMin,
            lowestMax: validationItem.limits.lowestMax,
            max: validationItem.limits.max,
            min: validationItem.limits.min,
          };

          if (isLocationCount(validationItem.metric)) {
            if (validationItem.column_id) {
              newValidationData.set(
                getConstraintValidationKey(validationItem.column_id, validationItem.column_value || EMPTY_GROUP_ID),
                validationEntry,
              );
            }
            else {
              newValidationData.set(LOCATION_COUNT_ID, validationEntry);
            }
          }
          else {
            newValidationData.set(validationItem.metric, validationEntry);
          }
        }

        setValidationData(newValidationData);
      }
    }
    catch (err) {
      if (!isApiError(err) || !isCancelError(err)) {
        setValidationData(new Map());
        setGeneralError(t('Something went wrong, please try again.'));
      }
    }
    finally {
      setIsLoading(false);
    }
  }, [clientId, mapId, spreadsheetId, constraints, boundaryGroupId, statesSubset, zipCodesSubset, t]);

  const wasStepActiveRef = useRef<boolean>(false);

  // Only changes when slider values change, not when checkboxes toggle.
  const boundsSignature = useMemo(() =>
    [...dataMetricsValues, ...demographicsValues]
      .map(c => `${c.id}|${c.constraintLowerBound ?? ''}|${c.constraintUpperBound ?? ''}`)
      .join(','),
  [dataMetricsValues, demographicsValues]);
  const validateConstraintsRef = useRef(validateConstraints);
  validateConstraintsRef.current = validateConstraints;

  useEffect(() => {
    if (!constraintsEnabled || !isStepActive) {
      wasStepActiveRef.current = isStepActive;
      return;
    }

    const stepJustBecameActive = !wasStepActiveRef.current;
    wasStepActiveRef.current = true;

    const cancelToken = createCancelToken();
    const validateTask = executeDebounced(() => validateConstraintsRef.current(cancelToken.token));

    if (stepJustBecameActive) {
      validateTask.now();
    }

    return () => {
      cancelToken.cancel();
    };
  }, [constraintsEnabled, boundsSignature, isStepActive]);

  return {
    areConstraintsValid: !isLoading && (constraintsEnabled
      ? Array.from(validationData.values()).every(validation => validation.errorMessages.length === 0)
      : true),
    generalError,
    isLoading,
    validationData,
  };
};

const metricForRequest = (metric: MetricValue): ValidateWMSTerritoryConstraintsItem => {
  const isTotalLocationCount = isTotalLocationCountMetric(metric.id);

  const base = {
    max: metric.constraintUpperBound ?? Number.MAX_SAFE_INTEGER,
    min: metric.constraintLowerBound ?? 0,
  };

  // Location count metric
  if (isTotalLocationCount) {
    return {
      ...base,
      column_id: null,
      column_value: null,
      metric: LOCATION_COUNT_ID,
    };
  }

  // Group/categorical metric
  if (metric.groupId) {
    return {
      ...base,
      column_id: metric.id,
      column_value: normalizeEmptyGroupIdForBackend(metric.groupId),
      metric: LOCATION_COUNT_ID,
    };
  }

  // Regular numeric column metric
  return {
    ...base,
    column_id: null,
    column_value: null,
    metric: metric.id,
  };
};
